import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, ArrowLeft, ArrowRight, Sparkles, IndianRupee, ShieldCheck, Camera, Upload, Zap, X, Wand2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { CONDITION_STEPS } from "@/constants/wizard";
import { api, API, formatApiError, formatINR } from "@/lib/api";
import { toast } from "sonner";

// Step indices: 0=storage, 1=ai, 2=screen, 3=body, 4=functional, 5=accessories, 6=summary
const AI_STEP = 1;
const CONDITION_START = 2;

export default function PricingWizard({ open, onClose, product, onBookPickup }) {
  const [step, setStep] = useState(0);
  const [storage, setStorage] = useState(null);
  const [answers, setAnswers] = useState({});
  const [aiFilledKeys, setAiFilledKeys] = useState([]); // which condition keys came from AI
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(0);
      setStorage(null);
      setAnswers({});
      setAiFilledKeys([]);
      setQuote(null);
    }
  }, [open, product]);

  const totalSteps = 2 + CONDITION_STEPS.length + 1; // storage + ai + 4 conditions + summary = 7
  const progress = ((step + 1) / totalSteps) * 100;

  const canNext = useMemo(() => {
    if (step === 0) return !!storage;
    if (step === AI_STEP) return true; // AI step is optional/skippable
    if (step >= CONDITION_START && step < CONDITION_START + CONDITION_STEPS.length) {
      return !!answers[CONDITION_STEPS[step - CONDITION_START].key];
    }
    return true;
  }, [step, storage, answers]);

  const fetchQuote = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/quote", {
        product_id: product.id,
        storage,
        conditions: answers,
      });
      setQuote(data);
      setStep(totalSteps - 1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (step === totalSteps - 2) {
      fetchQuote();
    } else {
      setStep((s) => s + 1);
    }
  };

  const handleAIResult = (result) => {
    setAnswers((a) => ({ ...a, screen: result.screen, body: result.body }));
    setAiFilledKeys(["screen", "body"]);
    toast.success(`AI analyzed — Screen: ${result.screen}, Body: ${result.body}`);
    // Jump directly to functional (step index CONDITION_START + 2)
    setStep(CONDITION_START + 2);
  };

  const handleSkipAI = () => setStep((s) => s + 1);

  if (!product) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto" data-testid="pricing-wizard-modal">
        <DialogHeader className="p-6 pb-4 border-b border-border">
          <DialogTitle className="text-xl">{product.name}</DialogTitle>
          <div className="text-xs text-muted-foreground font-mono uppercase tracking-widest">
            Step {step + 1} of {totalSteps}
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-secondary overflow-hidden">
            <motion.div
              className="h-full bg-primary"
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>
        </DialogHeader>

        <div className="p-6">
          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div key="storage" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <h4 className="text-lg font-semibold mb-1">Select Storage Variant</h4>
                <p className="text-sm text-muted-foreground mb-5">Pick the storage size printed on your device.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {product.storage_variants.map((v) => (
                    <button
                      key={v.label}
                      onClick={() => setStorage(v.label)}
                      data-testid={`storage-option-${v.label.replace(" ", "-")}`}
                      className={`rounded-xl border-2 p-4 text-left transition-all ${
                        storage === v.label
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/40"
                      }`}
                    >
                      <div className="font-semibold font-mono">{v.label}</div>
                      <div className="text-xs text-muted-foreground mt-1">Base</div>
                      <div className="text-accent font-bold font-mono">{formatINR(v.base_price)}</div>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {step === AI_STEP && (
              <AIPhotoStep key="ai" onDone={handleAIResult} onSkip={handleSkipAI} />
            )}

            {step >= CONDITION_START && step < CONDITION_START + CONDITION_STEPS.length && (
              <ConditionStepView
                key={`cond-${step}`}
                stepDef={CONDITION_STEPS[step - CONDITION_START]}
                value={answers[CONDITION_STEPS[step - CONDITION_START].key]}
                aiSuggested={aiFilledKeys.includes(CONDITION_STEPS[step - CONDITION_START].key)}
                onSelect={(v) => setAnswers((a) => ({ ...a, [CONDITION_STEPS[step - CONDITION_START].key]: v }))}
              />
            )}

            {step === totalSteps - 1 && quote && (
              <motion.div key="quote" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}>
                <div className="tv-chip-emerald mb-3">
                  <Sparkles className="w-3 h-3" /> AI Valuation Complete
                </div>
                <h4 className="text-lg font-semibold">Your instant offer</h4>
                <p className="text-sm text-muted-foreground">Price locked for 7 days. Cash / UPI paid on doorstep pickup.</p>

                <div className="mt-6 rounded-2xl bg-gradient-to-br from-primary to-sky-500 text-white p-6 relative overflow-hidden">
                  <div className="absolute inset-0 opacity-20 tv-grid-bg" />
                  <div className="relative">
                    <div className="text-xs font-mono uppercase tracking-widest opacity-80">
                      {quote.product_name} · {quote.storage}
                    </div>
                    <div className="mt-2 flex items-end gap-2">
                      <IndianRupee className="w-8 h-8" />
                      <span className="text-5xl font-extrabold font-mono leading-none" data-testid="instant-quote-final-price">
                        {quote.final_price.toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="mt-2 text-xs opacity-90 flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4" /> Guaranteed spot payment
                    </div>
                  </div>
                </div>

                <div className="mt-5">
                  <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Breakdown</div>
                  <div className="rounded-xl border border-border divide-y">
                    <div className="flex justify-between p-3 text-sm">
                      <span>Base Price</span>
                      <span className="font-mono font-semibold">{formatINR(quote.base_price)}</span>
                    </div>
                    {quote.breakdown.map((b, i) => (
                      <div key={i} className="flex justify-between p-3 text-sm">
                        <span className="text-muted-foreground">{b.label}</span>
                        <span className={`font-mono font-semibold ${b.amount < 0 ? "text-destructive" : "text-accent"}`}>
                          {b.amount > 0 ? "+" : ""}
                          {formatINR(b.amount)}
                        </span>
                      </div>
                    ))}
                    <div className="flex justify-between p-3 text-sm bg-accent/5">
                      <span className="font-semibold">Final Offer</span>
                      <span className="font-mono font-bold text-accent">{formatINR(quote.final_price)}</span>
                    </div>
                  </div>
                </div>

                <Button
                  className="w-full mt-6 h-12 bg-accent hover:bg-accent/90 text-accent-foreground"
                  onClick={() => onBookPickup({ product, storage, answers, quote })}
                  data-testid="book-pickup-btn"
                >
                  Book Free Doorstep Pickup <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {step !== totalSteps - 1 && (
          <div className="p-4 border-t border-border flex items-center justify-between bg-secondary/30">
            <Button
              variant="ghost"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              data-testid="wizard-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
            </Button>
            <Button
              onClick={handleNext}
              disabled={!canNext || loading}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="wizard-next-btn"
            >
              {step === totalSteps - 2 ? (loading ? "Calculating…" : "See Instant Offer") : step === AI_STEP ? "Skip · Answer Manually" : "Next"}
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AIPhotoStep({ onDone, onSkip }) {
  const [files, setFiles] = useState([]); // [{file, previewUrl}]
  const [analyzing, setAnalyzing] = useState(false);
  const inputRef = useRef(null);

  const addFiles = (list) => {
    const arr = Array.from(list).slice(0, 3 - files.length);
    const withPreviews = arr.map((f) => ({ file: f, previewUrl: URL.createObjectURL(f) }));
    setFiles((prev) => [...prev, ...withPreviews].slice(0, 3));
  };

  const removeAt = (idx) => {
    setFiles((prev) => {
      URL.revokeObjectURL(prev[idx].previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const analyze = async () => {
    if (files.length === 0) {
      toast.error("Upload at least one photo of your phone");
      return;
    }
    setAnalyzing(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("images", f.file));
      const res = await fetch(`${API}/ai/analyze-condition`, { method: "POST", body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `AI failed (${res.status})`);
      }
      const data = await res.json();
      onDone(data);
    } catch (err) {
      toast.error(err.message || "AI analysis failed. Please continue manually.");
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
    >
      <div className="tv-chip-emerald mb-3 inline-flex">
        <Sparkles className="w-3 h-3" /> AI-Powered · Gemini 3
      </div>
      <h4 className="text-lg font-semibold mb-1">Upload photos — skip 2 questions</h4>
      <p className="text-sm text-muted-foreground mb-5">
        Snap 2-3 clear photos (front screen, back, corners). Our AI will auto-detect screen & body condition in seconds.
      </p>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        capture="environment"
        onChange={(e) => addFiles(e.target.files)}
        className="hidden"
        data-testid="ai-file-input"
      />

      <div className="grid grid-cols-3 gap-3 mb-5">
        {files.map((f, i) => (
          <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-border bg-secondary group" data-testid={`ai-photo-${i}`}>
            <img src={f.previewUrl} alt="" className="w-full h-full object-cover" />
            <button
              onClick={() => removeAt(i)}
              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
              data-testid={`ai-photo-remove-${i}`}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        {files.length < 3 && (
          <button
            onClick={() => inputRef.current?.click()}
            disabled={analyzing}
            className="aspect-square rounded-xl border-2 border-dashed border-primary/40 hover:border-primary hover:bg-primary/5 transition flex flex-col items-center justify-center text-primary text-sm"
            data-testid="ai-add-photo-btn"
          >
            <Camera className="w-6 h-6 mb-1" />
            <span className="font-medium">Add photo</span>
            <span className="text-xs text-muted-foreground">{files.length}/3</span>
          </button>
        )}
      </div>

      <Button
        onClick={analyze}
        disabled={analyzing || files.length === 0}
        className="w-full h-11 bg-gradient-to-r from-primary to-sky-500 text-white hover:opacity-90 disabled:opacity-40"
        data-testid="ai-analyze-btn"
      >
        {analyzing ? (
          <><Wand2 className="w-4 h-4 mr-2 animate-pulse" /> AI is analyzing your photos…</>
        ) : (
          <><Zap className="w-4 h-4 mr-2" /> Analyze with AI ({files.length} photo{files.length !== 1 ? "s" : ""})</>
        )}
      </Button>

      <div className="mt-3 text-center">
        <button
          onClick={onSkip}
          className="text-sm text-muted-foreground hover:text-foreground underline"
          data-testid="ai-skip-btn"
        >
          Skip AI — I&apos;ll answer manually
        </button>
      </div>

      <div className="mt-5 flex items-start gap-2 text-xs text-muted-foreground bg-secondary/40 rounded-lg p-3">
        <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5 text-accent" />
        <span>
          Photos are analyzed instantly and never stored on our servers. You can always adjust the AI&apos;s answers before booking.
        </span>
      </div>
    </motion.div>
  );
}

function ConditionStepView({ stepDef, value, aiSuggested, onSelect }) {
  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <h4 className="text-lg font-semibold">{stepDef.title}</h4>
        {aiSuggested && (
          <span className="tv-chip-emerald text-[10px]">
            <Sparkles className="w-3 h-3" /> AI Suggested
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground mb-5">{stepDef.subtitle}</p>
      <div className="space-y-3">
        {stepDef.options.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => onSelect(opt.value)}
              data-testid={`condition-${stepDef.key}-${opt.value}`}
              className={`w-full flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                active ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
              }`}
            >
              <div
                className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                  active ? "border-primary bg-primary" : "border-muted-foreground/40"
                }`}
              >
                {active && <Check className="w-3 h-3 text-primary-foreground" />}
              </div>
              <div className="flex-1">
                <div className="font-semibold text-sm sm:text-base">{opt.label}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{opt.desc}</div>
              </div>
              <span
                className={`text-xs font-mono font-bold px-2 py-1 rounded-md shrink-0 ${
                  opt.tag.startsWith("+")
                    ? "bg-accent/10 text-accent"
                    : opt.tag === "Best"
                    ? "bg-primary/10 text-primary"
                    : "bg-destructive/10 text-destructive"
                }`}
              >
                {opt.tag}
              </span>
            </button>
          );
        })}
      </div>
    </motion.div>
  );
}
